import DetailedImage from "components/admin/UI/DetailedImage";
import HeaderRenderer from "components/admin/UI/InfoTooltip";
import { formatCurrency } from "utils/common";

export const rolePrefix = `${process.env.REACT_APP_AUTH0_AUDIENCE}/roles`;
export const ADMIN_ROLE = "Admin";

export const CACHE_KEYS = {
  COMPANY: "COMPANY",
  NEWSLETTER: "NEWSLETTER",
  STAY_UPDATED: "STAY_UPDATED",
  SHARE_HOLDERS: "SHARE_HOLDERS",
  WALLET_SELL: "WALLET_SELL",
  WALLET_BUY: "WALLET_BUY",
  NDA: "NDA",
  COMPANY_WALLET: "COMPANY_WALLET",
  SURVEY: "SURVEY",
  STOCKS: "STOCKS",
  PAGINATED_USERS: "PAGINATED_USERS",
  NOTIFICATION: "NOTIFICATION",
  QUESTIONER: "QUESTIONER",
  QUESTIONER_ANSWER: "QUESTIONER_ANSWER",
  CONFIRMATION_SETTINGS: "CONFIRMATION_SETTINGS",
};

// TERMS_OF_USE and PRIVACY_POLICY are the PDFs the footer already linked to.
// COOKIE_POLICY is served from public/, so it sits outside the Auth0-gated app
// and is readable without logging in. Relative so staging resolves to staging.
export const LEGAL_URLS = {
  TERMS_OF_USE:
    "https://valoo.io/wp-content/uploads/2022/11/terms_of_use7.7.2020.pdf",
  PRIVACY_POLICY:
    "https://valoo.io/wp-content/uploads/2023/03/%D7%9E%D7%93%D7%99%D7%A0%D7%99%D7%95%D7%AA-%D7%A4%D7%A8%D7%98%D7%99%D7%95%D7%AA-23.7.2020.pdf",
  COOKIE_POLICY: "/cookie-policy.html",
};

export const PAGINATION_OPTIONS = [10, 25, 50, 100];

export const MODAL_TYPE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
  VIEW: "view",
  UPDATE: "update",
  VIEW_GUEST: "view_guest",
};

export const MODEL_INITIAL_STATE = {
  [MODAL_TYPE.CREATE]: false,
  [MODAL_TYPE.EDIT]: false,
  [MODAL_TYPE.DELETE]: false,
  [MODAL_TYPE.UPDATE]: false,
  [MODAL_TYPE.VIEW]: false,
  [MODAL_TYPE.VIEW_GUEST]: false,
};

export const VIEW_MODAL_INITIAL_STATE = {
  ...MODEL_INITIAL_STATE,
  [MODAL_TYPE.VIEW]: false,
};

export const COMPANY_THUMBNAIL_COLUMN = [
  {
    flex: 0.5,
    field: "company",
    headerName: "Company",
    sortable: false,
    renderCell: (params) => {
      const company = params.row.company;
      if (!company) return <></>;
      return (
        <DetailedImage
          image={company.image_url}
          title={company.company_name}
          subtitle={company.listing_preference}
        />
      );
    },
  },
];

export const USER_THUMBNAIL_COLUMN = [
  {
    flex: 0.5,
    minWidth: 300,
    field: "user",
    headerName: "User",
    sortable: false,
    renderCell: (params) => {
      const user = params.row.user;
      if (!user) return <></>;
      return <DetailedImage title={user.fullname} subtitle={user.email} />;
    },
  },
];

export const COMPANY_USER_THUMBNAIL_COLUMN = [
  ...USER_THUMBNAIL_COLUMN,
  ...COMPANY_THUMBNAIL_COLUMN,
];

export const COMMON_WALLET_COLUMNS = [
  {
    flex: 0.2,
    field: "id",
    headerName: "#",
    headerAlign: "center",
    align: "center",
  },
  ...COMPANY_USER_THUMBNAIL_COLUMN,
  {
    flex: 0.3,
    field: "number_of_stocks",
    headerName: "No. of Stocks",
    crmKey: "number_of_stocks",
    renderHeader: HeaderRenderer,
  },
  {
    flex: 0.3,
    field: "price_per_stock",
    headerName: "No. of Stocks",
    valueGetter: (v) => formatCurrency(v),
    crmKey: "price_per_stock",
    renderHeader: HeaderRenderer,
  },
  {
    flex: 0.3,
    field: "steps",
    headerName: "Steps",
    crmKey: "steps",
    renderHeader: HeaderRenderer,
  },
];

export const TITLE_CONTENT_DEFAULT_ENTRY = {
  title: "",
  content: "",
};

export const IMAGE_DEFAULT_ENTRY = {
  image: null,
};

export const TITLE_IMAGE_CONTENT_DEFAULT_ENTRY = {
  ...TITLE_CONTENT_DEFAULT_ENTRY,
  ...IMAGE_DEFAULT_ENTRY,
};

export const NOTIFICATION_TYPE = {
  CREATE_OFFER: "CREATE_OFFER",
  UPLOAD_SIGNED_DOCUMENT: "UPLOAD_SIGNED_DOCUMENT",
  CREATE_SELL_OFFER: "CREATE_SELL_OFFER",
  UPLOAD_WITHOLDING_TAX_CERTIFICATE: "UPLOAD_WITHOLDING_TAX_CERTIFICATE",
  UPLOAD_ID: "UPLOAD_ID",
  UPLOAD_ACCOUNT_CERTIFICATE: "UPLOAD_ACCOUNT_CERTIFICATE",
};

export const NOTIFICATION_ENTITY = {
  UserWalletBuy: "UserWalletBuy",
  UserWalletSell: "UserWalletSell",
};

export const DRAG_TYPES = {
  FORM_COMPONENT: "FORM_COMPONENT",
  FIELD_REORDER: "FIELD_REORDER",
  FIELD_MOVE: "FIELD_MOVE",
};

export const QUESTIONER_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
  OUTDATED: "outdated",
};

export const USER_SURVEY_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  IN_PROGRESS: "in_progress",
  OUTDATED: "outdated",
};

export const QUESTIONNAIRE_TYPES = {
  PUBLIC: "public",
  PRIVATE: "private",
};

export const questionVisiblityOptions = [
  {
    label:"Public",
    value:QUESTIONNAIRE_TYPES.PUBLIC
  },
  {
    label:"Private",
    value:QUESTIONNAIRE_TYPES.PRIVATE
  }
]

export const exampleFormJson = {
  title: "Customer Insights Survey",
  steps: [
    {
      id: "step1",
      title: "General Information",
      fields: [
        {
          id: "field-9876543210001",
          crmKey: "field-9876543210001",
          type: "TEXT",
          label: "What is your full name?",
          placeholder: "Enter your name",
          required: true,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
        },
        {
          id: "field-9876543210002",
          crmKey: "field-9876543210002",
          type: "EMAIL",
          label: "Enter your email address",
          placeholder: "yourname@example.com",
          required: true,
          dependencies: null,
          validation: {
            format: "email",
          },
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
        },
        {
          id: "field-9876543210003",
          crmKey: "field-9876543210003",
          type: "SELECT",
          label: "What is your business category?",
          placeholder: "",
          required: true,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
          options: [
            {
              label: "E-commerce",
              value: "ecommerce",
            },
            {
              label: "Real Estate",
              value: "real_estate",
            },
            {
              label: "Automotive",
              value: "automotive",
            },
            {
              label: "Other",
              value: "other",
            },
          ],
        },
      ],
    },
    {
      id: "step2",
      title: "Business Preferences",
      fields: [
        {
          id: "field-9876543210004",
          crmKey: "field-9876543210004",
          type: "MULTISELECT",
          label: "Which marketing channels do you use?",
          placeholder: "",
          required: true,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
          options: [
            {
              label: "Social Media",
              value: "social_media",
            },
            {
              label: "Email Marketing",
              value: "email_marketing",
            },
            {
              label: "SEO",
              value: "seo",
            },
            {
              label: "Paid Ads",
              value: "paid_ads",
            },
          ],
        },
        {
          id: "field-9876543210005",
          crmKey: "field-9876543210005",
          type: "CHECKBOX",
          label: "Do you use a CRM tool?",
          placeholder: "",
          required: false,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
          defaultChecked: false,
        },
        {
          id: "field-9876543210006",
          crmKey: "field-9876543210006",
          type: "SLIDER",
          label: "Rate your business growth over the last year (1-10)",
          placeholder: "",
          required: true,
          dependencies: null,
          validation: {
            min: 1,
            max: 10,
          },
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
          min: 1,
          max: 10,
        },
      ],
    },
    {
      id: "step3",
      title: "Future Plans & Feedback",
      fields: [
        {
          id: "field-9876543210007",
          crmKey: "field-9876543210007",
          type: "TEXTAREA",
          label: "What are your future expansion plans?",
          placeholder: "Share your thoughts...",
          required: false,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
        },
        {
          id: "field-9876543210008",
          crmKey: "field-9876543210008",
          type: "DATEPICKER",
          label: "When did you start your business?",
          placeholder: "",
          required: true,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
        },
        {
          id: "field-9876543210009",
          crmKey: "field-9876543210009",
          type: "IMAGEUPLOAD",
          label: "Upload a photo of your business premises",
          placeholder: "",
          required: false,
          dependencies: null,
          validation: null,
          visibleUserTypes: ["seller", "buyer", "buyerandseller", "company"],
          accept: "image/*",
          maxSize: 5000000,
        },
      ],
    },
  ],
};

export const apiUrl = process.env.REACT_APP_API_ENDPOINT;

export const SOCKET_EVENTS = {
  CONNECTION: "CONNECTION",
  NEW_SURVEY_PUBLISHED: "NEW_SURVEY_PUBLISHED",
};

export const STORAGE_KEYS = {
  FORM_DATA: "formData",
  ACTIVE_STEP: "activeStep",
  SESSION_GUEST_STEP: "sessionGuestStep",
};
